import type { LiveErrorPayload, LiveTextInputAckPayload } from './live-call';

/** 单次文字输入允许提交的最大字符数。 */
export const MAX_LIVE_CHAT_TEXT_LENGTH = 200;

/** 截断为最多指定字符数；Array.from 按 Unicode code point 计数，避免截断半个 emoji。 */
export const truncateLiveChatText = (text: string, maxLength = MAX_LIVE_CHAT_TEXT_LENGTH): string =>
  Array.from(text).slice(0, maxLength).join('');

/**
 * 用户消息状态机（只显示用户自己发的文字，AI 只回语音不回文字）：
 * - pending：已发出但尚未通过审核/收到服务端 ack，不进入回显快照
 * - sent：服务端已回 text_input_ack（accepted），确认收到并投递 AI
 * - failed：发送失败（WS 未就绪 / send 抛错）或服务端 error 收口，允许重发
 */
export type LiveChatUserMessageStatus = 'pending' | 'sent' | 'failed';

export interface LiveChatMessage {
  /** 稳定去重键：客户端生成的 client_msg_id。 */
  id: string;
  content: string;
  status: LiveChatUserMessageStatus;
  /** 创建时间戳（ms），用于排序与渐隐计时。 */
  createdAt: number;
  /** 服务端错误码（failed 时可能带上）。 */
  errorCode?: string;
}

const nowMs = (): number => Date.now();

/**
 * 纯内存文本聊天状态：只承载“用户自己发的文字气泡”。
 * AI 只回语音、不回文字，所以这里没有 AI 消息 / delta 聚合。
 * 负责：暂存 pending（审核确认前不回显）、ack→sent、error→failed、重发时重置 pending。
 * 变更后通过 onChange 推送最新有序快照。
 */
export class LiveChatStore {
  private readonly onChange: (messages: LiveChatMessage[]) => void;
  private messages = new Map<string, LiveChatMessage>();
  private order: string[] = [];

  constructor(onChange: (messages: LiveChatMessage[]) => void) {
    this.onChange = onChange;
  }

  /** 用户发送：乐观插入 pending 消息。id 为客户端生成的 client_msg_id。 */
  addPendingUserMessage(id: string, content: string): void {
    const existing = this.messages.get(id);
    if (existing) {
      // 重发：复用同一条，重置为 pending，清掉旧错误码。
      this.messages.set(id, { ...existing, status: 'pending', errorCode: undefined });
      this.emit();
      return;
    }

    this.messages.set(id, {
      id,
      content,
      status: 'pending',
      createdAt: nowMs(),
    });
    this.order.push(id);
    this.emit();
  }

  /** 标记用户消息状态（pending→sent/failed）。id 不存在则忽略。 */
  setUserMessageStatus(id: string, status: LiveChatUserMessageStatus, errorCode?: string): void {
    const existing = this.messages.get(id);
    if (!existing) {
      return;
    }

    this.messages.set(id, { ...existing, status, errorCode: errorCode ?? existing.errorCode });
    this.emit();
  }

  /**
   * 收到服务端 text_input_ack（accepted）。把对应 pending 用户消息翻成 sent。
   * accepted 非 true 时忽略（失败由 error=15 收口）。
   */
  applyAck(payload: LiveTextInputAckPayload): void {
    const clientMsgId = payload.client_msg_id?.trim();
    if (!clientMsgId || payload.accepted !== true) {
      return;
    }
    // 已 failed 的不因迟到 ack 翻回 sent（失败态更重要，用户可能已重发）。
    const existing = this.messages.get(clientMsgId);
    if (!existing || existing.status === 'failed') {
      return;
    }
    this.setUserMessageStatus(clientMsgId, 'sent');
  }

  /**
   * 收到服务端 error（error=15）。带 msg_id 时将对应用户消息收口为 failed，供 UI 展示重发。
   * msg_id 缺失时忽略（连接级错误由上层连接状态处理）。
   */
  applyError(payload: LiveErrorPayload): void {
    const msgId = payload.msg_id?.trim();
    if (!msgId) {
      return;
    }
    this.setUserMessageStatus(msgId, 'failed', payload.code?.trim() || undefined);
  }

  /** 移除一条消息（用于气泡渐隐后从列表清除）。 */
  removeMessage(id: string): void {
    if (!this.messages.has(id)) {
      return;
    }
    this.messages.delete(id);
    this.order = this.order.filter((item) => item !== id);
    this.emit();
  }

  getMessages(): LiveChatMessage[] {
    return this.order
      .map((id) => this.messages.get(id))
      .filter((msg): msg is LiveChatMessage => msg !== undefined && msg.status !== 'pending');
  }

  clear(): void {
    this.messages.clear();
    this.order = [];
    this.emit();
  }

  private emit(): void {
    this.onChange(this.getMessages());
  }
}
